import { useMemo, useState } from "react";

export type SortColumn<T> = {
  key: keyof T;
  defaultSortDir?: "asc" | "desc";
  comparator?: (a: T, b: T, sortDir: "asc" | "desc") => number;
};

export function useSortedData<T>(data: T[], columns: SortColumn<T>[] = [], defaultSortBy?: keyof T) {
  const [sortBy, setSortBy] = useState<keyof T>(defaultSortBy || columns[0].key);
  const [sortDir, setSortDir] = useState<"asc" | "desc">(columns.find(col => col.key === sortBy)?.defaultSortDir || "asc");

  const handleSetSortBy = (columnKey: keyof T) => {
    if (sortBy === columnKey) {
      // Toggle sort direction
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(columnKey);
      setSortDir(columns.find(col => col.key === columnKey)?.defaultSortDir || "asc");
    }
  };

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      // Is custom comparator provided for the current column?
      const currentColumn = columns.find(col => col.key === sortBy);
      if (currentColumn?.comparator) {
        return currentColumn.comparator(a, b, sortDir);
      }

      // Default sorting logic based on value type

      const aValue = a[sortBy];
      const bValue = b[sortBy];

      // Put empty values last
      if (!!aValue && !bValue) return sortDir === "asc" ? -1 : 1;
      if (!aValue && !!bValue) return sortDir === "asc" ? 1 : -1;

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDir === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      } else {
        const aNumber = Number(aValue) || 0;
        const bNumber = Number(bValue) || 0;
        return sortDir === "asc" ? aNumber - bNumber : bNumber - aNumber;
      }
    });
  }, [data, sortBy, sortDir, columns]);

  return { data: sortedData, sortBy, sortDir, setSortBy: handleSetSortBy, setSortDir };
}
